import { CreditCardIcon, PlusIcon } from 'lucide-react'
import { useState } from 'react'

import { ApiError, errorMessage } from '@/api/client'
import { usePaymentMethods, useRemoveCard, useSetDefaultCard } from '@/api/queries'
import type { PaymentMethodCard } from '@/api/types'
import { AddCardDialog } from '@/components/billing/AddCardDialog'
import { ErrorState, TableSkeleton } from '@/components/states'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  cardErrorText,
  cardLabel,
  cardListState,
  expiryLabel,
  isExpired,
  orderedCards,
  removeDialogText,
  removeHint,
} from '@/lib/cards'

/** Several cards, one default (BI11); removing the last one is refused by the server (BI12). */
export function PaymentMethodsCard({ readOnly }: { readOnly: boolean }) {
  const cards = usePaymentMethods()
  const setDefault = useSetDefaultCard()
  const remove = useRemoveCard()
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<PaymentMethodCard | null>(null)

  const list = cards.data ?? []
  const state = cardListState(list)
  const error = remove.error ?? setDefault.error
  const code = error instanceof ApiError ? error.code : undefined

  return (
    <Card id="payment-methods" data-testid="payment-methods">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <CreditCardIcon className="size-4 text-primary" />
            Payment methods
          </span>
          {readOnly ? null : (
            <Button
              size="sm"
              variant="outline"
              data-testid="add-card"
              onClick={() => {
                setAdding(true)
              }}
            >
              <PlusIcon className="size-3.5" />
              Add card
            </Button>
          )}
        </CardTitle>
        <CardDescription>
          The default card pays renewals and police auto-recharge. Card details are entered at Stripe; saltseed
          never sees them.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {cards.isPending ? (
          <TableSkeleton rows={2} columns={2} />
        ) : cards.isError ? (
          <ErrorState error={cards.error} title="Could not load your cards" onRetry={() => void cards.refetch()} />
        ) : state === 'empty' ? (
          <p className="text-sm text-muted-foreground" data-testid="no-cards">
            No card on file yet. Add one to pay for a plan or to turn on auto-recharge.
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border" data-testid="card-list">
            {orderedCards(list).map((card) => (
              <li key={card.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5" data-testid="card-row">
                <span className="text-sm font-medium">{cardLabel(card)}</span>
                <span className="text-xs text-muted-foreground">expires {expiryLabel(card)}</span>
                {card.is_default ? <Badge data-testid="default-badge">default</Badge> : null}
                {isExpired(card) ? <Badge variant="destructive">expired</Badge> : null}
                {readOnly ? null : (
                  <span className="ml-auto flex gap-2">
                    {card.is_default ? null : (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={setDefault.isPending}
                        data-testid="set-default"
                        onClick={() => {
                          remove.reset()
                          setDefault.mutate(card.id)
                        }}
                      >
                        Set default
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive"
                      data-testid="remove-card"
                      onClick={() => {
                        setDefault.reset()
                        remove.reset()
                        setRemoving(card)
                      }}
                    >
                      Remove
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {state === 'single' && !readOnly ? (
          <p className="text-xs text-muted-foreground" data-testid="last-card-hint">
            {removeHint(list)}
          </p>
        ) : null}
        {error === null || removing !== null ? null : (
          <Alert variant="destructive" data-testid="card-error">
            <AlertDescription>{cardErrorText(code, errorMessage(error))}</AlertDescription>
          </Alert>
        )}
      </CardContent>

      {adding ? (
        <AddCardDialog
          firstCard={list.length === 0}
          onClose={() => {
            setAdding(false)
          }}
        />
      ) : null}
      {removing === null ? null : (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next && !remove.isPending) setRemoving(null)
          }}
        >
          <DialogContent data-testid="remove-card-dialog">
            <DialogHeader>
              <DialogTitle>Remove {cardLabel(removing)}?</DialogTitle>
              <DialogDescription>
                {removeDialogText(removing, list.length)}
              </DialogDescription>
            </DialogHeader>
            {remove.isError ? (
              <Alert variant="destructive" data-testid="remove-card-error">
                <AlertDescription>{cardErrorText(code, errorMessage(remove.error))}</AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button variant="ghost" disabled={remove.isPending} onClick={() => { setRemoving(null) }}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={remove.isPending}
                data-testid="remove-card-confirm"
                onClick={() => {
                  remove.mutate(removing.id, {
                    onSuccess: () => {
                      setRemoving(null)
                    },
                  })
                }}
              >
                {remove.isPending ? 'Removing…' : 'Remove card'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  )
}
